import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiPrintPage, MiPrintTable } from "@miplatform/assets";
import { MiBoxSelector, MiChip, MiModal } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  PRINT_OPTIONS,
} from "@miplatform/constants";
import { mdiPrinterOutline } from "@mdi/js";
import { Box, Typography } from "@mui/material";

/**
 * OrdersPrintModal Component
 *
 * A modal component that allows users to select and print order details.
 *
 * @component
 * @param {Object} props - The component props
 * @param {boolean} props.open - Determines if the modal is open
 * @param {Object} props.selectedProduct - The selected product for printing
 * @param {boolean} props.isLoading - Indicates if the print action is loading
 * @param {Function} props.onPrint - Callback function when printing is initiated
 * @param {Function} props.onClose - Callback function when the modal is closed
 * @returns {JSX.Element} The rendered OrdersPrintModal component
 */
export const OrdersPrintModal = ({
  open,
  selectedOrderTemplate,
  isLoading,
  onPrint,
  onClose,
}) => {
  const { classes } = useStyles();
  const [selectedOption, setSelectedOption] = useState(PRINT_OPTIONS.TABLE);

  const handleClose = () => {
    onClose();
  };

  const handlePrint = () => {
    onPrint(selectedOption);
    onClose();
  };

  return (
    <MiModal
      open={open}
      closeModal={handleClose}
      title="Print Orders"
      titleIcon={mdiPrinterOutline}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          color: BUTTON_COLORS.BLACK,
          size: "medium",
          onClick: handleClose,
        },
        {
          name: "Print",
          variant: BUTTON_VARIANTS.CONTAINED,
          size: "medium",
          color: BUTTON_COLORS.PRIMARY,
          onClick: handlePrint,
          disabled: isLoading,
        },
      ]}
      topRightClose={handleClose}
      loading={isLoading}
    >
      {selectedOrderTemplate && (
        <>
          <Typography className={classes.title}>Selected Order Type</Typography>
          <Box className={classes.chipsContainer}>
            <MiChip
              className={classes.chip}
              label={selectedOrderTemplate.label}
            />
          </Box>
        </>
      )}
      <Typography className={classes.title}>Print Type</Typography>
      <Box className={classes.selectorsContainer}>
        <MiBoxSelector
          label="Order Per Page"
          selected={selectedOption === PRINT_OPTIONS.PAGES}
          icon={<img src={MiPrintPage} alt="Print pages" />}
          onChange={() => setSelectedOption(PRINT_OPTIONS.PAGES)}
        />
        <MiBoxSelector
          label="Table List View"
          selected={selectedOption === PRINT_OPTIONS.TABLE}
          icon={<img src={MiPrintTable} alt="Print table" />}
          onChange={() => setSelectedOption(PRINT_OPTIONS.TABLE)}
        />
      </Box>
    </MiModal>
  );
};

const useStyles = makeStyles()((theme) => ({
  title: {
    fontSize: theme.fontSize.medium,
    fontWeight: 600,
    paddingBottom: theme.spacing(1),
  },
  chip: {
    borderRadius: theme.spacing(1.5),
  },
  chipsContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    maxHeight: 150,
    overflowY: "auto",
    paddingBottom: theme.spacing(1),
  },
  selectorsContainer: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
  },
}));
